// Copyright (c) 2026 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { FilterMenuButton } from '@/components/filter-menu-button';
import { DateRangeShortcut } from '@/constants/date-range-shortcut';
import { Button, Popover, Stack } from '@mui/material';
import { DateRange, StaticDateRangePicker } from '@mui/x-date-pickers-pro';
import { DateTime } from 'luxon';
import { useMemo, useRef, useState } from 'react';

interface Props {
  label: string;
  value: DateRange<DateTime>;
  onChange: (value: DateRange<DateTime>) => void;
  shortcuts: DateRangeShortcut[];
}

export function FilterDateRangePopover(props: Props) {
  const { label, value, onChange, shortcuts } = props;
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);

  const isActive = value[0] !== null || value[1] !== null;
  const badgeCount = isActive ? 1 : 0;

  const today = useMemo(() => DateTime.local().startOf('day'), []);
  const pickerShortcuts = useMemo(
    () =>
      shortcuts.map((shortcut) => ({
        label: shortcut.label,
        getValue: () => shortcut.getDateRange(today),
      })),
    [shortcuts, today]
  );

  return (
    <>
      <FilterMenuButton
        ref={anchorRef}
        count={badgeCount}
        isDisabled={false}
        onClick={() => setOpen((previous) => !previous)}
      >
        {label}
      </FilterMenuButton>
      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <StaticDateRangePicker
          value={value}
          onChange={onChange}
          calendars={2}
          slotProps={{
            toolbar: { toolbarFormat: 'MMMM dd, yyyy' },
            shortcuts: { items: pickerShortcuts },
            actionBar: { actions: [] },
          }}
        />
        <Stack direction="row" justifyContent="flex-end" px={2} pb={1.5}>
          <Button
            size="small"
            onClick={() => {
              onChange([null, null]);
              setOpen(false);
            }}
          >
            Clear
          </Button>
        </Stack>
      </Popover>
    </>
  );
}
